import { AsyncPipe, NgClass } from '@angular/common'
import { Component, EventEmitter, OnInit, Input, Output, OnDestroy } from '@angular/core'
import { BehaviorSubject, Subject, Subscription, takeUntil, takeWhile, timer } from 'rxjs'
import { NgCircleProgressModule } from 'ng-circle-progress'
import { toDefaultNumber } from '@nx-superprep/utils'
import { Choice } from '@nx-superprep/backend/firebase'

export type PlayButtonActionType = 'start' | 'stop' | Choice | Choice[] | undefined

@Component({
  standalone: true,
  selector: 'nx-superprep-play-button',
  templateUrl: './play-button.component.html',
  styleUrls: ['./play-button.component.scss'],
  imports: [AsyncPipe, NgClass, NgCircleProgressModule],
})
export class PlayButtonComponent implements OnInit, OnDestroy {
  @Output() action = new EventEmitter<PlayButtonActionType>()

  private _duration = 0
  @Input() get duration() {
    return this._duration
  }
  set duration(value) {
    this._duration = toDefaultNumber(value, 0)
    if (value > 0) { this.start() }
  }

  private _diameter = 50
  @Input() get diameter() {
    return this._diameter
  }
  set diameter(value) {
    this._diameter = toDefaultNumber(value, 50)
  }

  private _imageStart = 'assets/gameplay/play-icon.svg'
  @Input() get imageStart() {
    return this._imageStart
  }
  set imageStart(value) {
    this._imageStart = value
  }

  private _imageStop = 'assets/gameplay/replay-icon.svg'
  @Input() get imageStop() {
    return this._imageStop
  }
  set imageStop(value) {
    this._imageStop = value
  }

  private _imageError = 'assets/gameplay/error-icon.svg'
  @Input() get imageError() {
    return this._imageError
  }
  set imageError(value) {
    this._imageError = value
  }

  protected readonly imageUrl: BehaviorSubject<string> = new BehaviorSubject<string>(
    this.isError ? this.imageError : this.imageStart
  )

  countUp: Subscription | undefined
  progress = -1

  protected readonly onDestroy = new Subject<void>()

  get isError() {
    return this.duration === -1
  }
  get isPlayable() {
    return this.duration > 0
  }
  get inProgress() {
    return this.progress >= 0 && this.progress < 100
  }
  get isDone() {
    return this.progress === 100
  }

  get radius() {
    return 5 + this.diameter / 2
  }
  get imageHeight() {
    return this.diameter
  }
  get imageWidth() {
    return this.diameter
  }

  get outerStrokeColor() {
    return this.isPlayable ? '#f5deb3' : 'transparent'
  }
  get outerStrokeGradientStopColor() {
    return this.isPlayable ? '#f5deb3' : 'transparent'
  }

  get animationTime() {
    return () => (this.duration > 0 ? this.duration * 10 : 100)
  }

  ngOnDestroy(): void {
    this.onDestroy.next()
    this.onDestroy.complete()
    this.imageUrl.complete()
  }

  ngOnInit() {
    if (this.isPlayable && !this.inProgress) { this.reset() }
  }

  start() {
    if (this.inProgress) { return }
    if (this.isError) { this.imageUrl.next(this.imageError); return }
    this.countUp?.unsubscribe()
    this.progress = 0
    this.imageUrl.next(this.imageStart)
    this.action.emit('start')
    this.countUp = timer(0, this.animationTime())
      .pipe(takeUntil(this.onDestroy), takeWhile(() => !this.isDone, true))
      .subscribe(() => {
        if (this.isDone) { this.onTrackComplete() }
        this.progress++
      })
  }

  onTrackComplete() {
    this.imageUrl.next(this.imageStop)
    this.action.emit('stop')
  }

  reset() {
    this.onDestroy.next()
    this.progress = -1
    this.imageUrl.next(this.isError ? this.imageError : this.imageStart)
  }

  onClick() {
    if (!this.isPlayable || this.inProgress) { return}
    this.reset()
    this.action.emit()
  }
}
